import sortBy from "lodash/sortBy";

import { BomComponent } from "@/features/job-tracking/queries/tracking-data/bom-by-component";
import { ItemReceipt } from "@/features/job-tracking/queries/tracking-data/item-receipts";
import { PurchaseOrder, PurchaseOrderLineItem } from "@/features/job-tracking/queries/tracking-data/purchase-orders";
import { Bin } from "@/features/job-tracking/queries/tracking-data/sales-order-rack";
import { ItemType } from "@/types/sos-types";

export interface ComponentPurchasingProgress {
  component: BomComponent;
  /** A sort index to use based on the item type */
  sortIndexForType: number;
  purchaseOrderLines: PurchaseOrderLineItem[];
  quantitiesByPo: Map<PurchaseOrder, number>;
  quantitiesByReceipt: Map<ItemReceipt, number>;
  /** The total quantity on POs that have a green star. POs with any other star color are not considered ordered */
  numOrdered: number;
  numReceived: number;
  numReceivedToSoRack: number;
}

/** Determines the sort order for item types */
const sortIndexForType: Record<ItemType, number> = {
  "Inventory Item": 0,
  Assembly: 1,
  Other: 2,
  Overhead: 3,
  Service: 4,
  Expense: 5,
  "Non-inventory Item": 6,
  Labor: NaN,
  Category: NaN,
  "Item Group": NaN,
};

export function getPurchasingProgress(
  bomComponents: BomComponent[],
  itemReceipts: ItemReceipt[],
  purchaseOrders: PurchaseOrder[],
  salesOrderRack: Bin,
): ComponentPurchasingProgress[] {
  // Group the line items by item ID for faster lookup
  const poLinesByItemId = Map.groupBy(
    purchaseOrders.flatMap((po) => po.jobLines.filter((line) => !!line.uomQuantity).map((line) => ({ ...line, po }))),
    (line) => line.item.id,
  );
  const receiptLinesByItemid = Map.groupBy(
    itemReceipts.flatMap((receipt) =>
      receipt.jobLines.filter((line) => !!line.uomQuantity).map((line) => ({ ...line, receipt })),
    ),
    (line) => line.item.id,
  );

  // Filter the BOM components to only those that need to be purchased, or are not on the BOM but have been purchased or
  // received for this current job anyway
  const relevantComponents = bomComponents.filter((x) => needToPurchase(x, poLinesByItemId, receiptLinesByItemid));

  // For each relevant component, create a PurchasingProgress record
  const purchasingProgressRecords = relevantComponents.map<ComponentPurchasingProgress>((component) => {
    const item = component.item;
    const preferredVendorName = item.preferredVendor?.name;

    // Get a map of POs containing this item, and the quantity of that item on the PO
    const poLines = poLinesByItemId.get(item.id) || [];
    const linesByPo = Map.groupBy(poLines, (line) => line.po);
    const quantitiesByPo = new Map<PurchaseOrder, number>(
      [...linesByPo.entries()].map(([po, lines]) => [po, lines.reduce((sum, line) => sum + line.uomQuantity, 0)]),
    );

    // Get a map of item receipts containing this item, and the quantity of that item on the receipt
    const receiptLines = receiptLinesByItemid.get(item.id) || [];
    const linesByReceipt = Map.groupBy(receiptLines, (line) => line.receipt);
    const quantitiesByReceipt = new Map<ItemReceipt, number>(
      [...linesByReceipt.entries()].map(([receipt, lines]) => [
        receipt,
        lines.reduce((sum, line) => sum + line.uomQuantity, 0),
      ]),
    );

    const numOrdered = poLines.filter((x) => x.po.starEmoji === "🟢").reduce((sum, line) => sum + line.baseQuantity, 0);
    const numReceived = receiptLines.reduce((sum, line) => sum + line.baseQuantity, 0);
    const numReceivedToSoRack = receiptLines
      .filter((x) => x.bin?.id === salesOrderRack.id)
      .reduce((sum, line) => sum + line.baseQuantity, 0);
    return {
      component,
      sortIndexForType: sortIndexForType[item.type],
      preferredVendorName,
      purchaseOrderLines: poLines,
      quantitiesByPo,
      quantitiesByReceipt,
      numOrdered,
      numReceived,
      numReceivedToSoRack,
    };
  });

  // Sort first by type, then by preferred vendor, then by item name
  return sortBy(
    purchasingProgressRecords,
    (x) => x.sortIndexForType,
    (x) => x.component.item.preferredVendor?.name,
    (x) => x.component.item.name,
  );
}

/**
 * Determine if the provided BOM item needs to be purchased.
 *
 * If `poLinesByItemId` and `receiptLinesByItemId` are provided, an item may also be included if it has already been
 * purchased or received for this job (even though it does not appear on the sales order or is an assembly).
 */
export function needToPurchase(
  bomItem: BomComponent,
  poLinesByItemId?: Map<number, unknown>,
  receiptLinesByItemId?: Map<number, unknown>,
) {
  // Labour is not purchased, it is used as part of in-house builds
  if (bomItem.item.type === "Labor") return false;

  // If the item is not on the sales order BOM, or is an assembly, it is only included if it can be found on a purchase order or item receipt
  // NOTE: Assemblies are not normally purchased. If we outsource their manufacturing, then the associated -CM item goes on the PO, not the assembly itself
  if (!bomItem.onSalesOrder || bomItem.isAssembly) {
    return poLinesByItemId?.has(bomItem.item.id) || receiptLinesByItemId?.has(bomItem.item.id);
  }

  // No need to purchase items that have zero quantity on the BOM
  // NOTE: Items that are fully being pulled from existing inventory will still show up, but this is helpful to the
  // purchaser to indicate why they don't need to purchase that part
  // NOTE: Items with zero quantity on the BOM appear in an error report to indicate that something is likely wrong
  if (bomItem.quantityRequiredForJob <= 0) return false;

  // TODO: -CM items are required to be purchased, but in some cases we may decide to build the associated assembly
  // in-house rather than outsourcing it. To support this use case, we need to check if the full quantity for the
  // associated assembly has already been issued on work orders or build. If yes, then we don't need to purchase the
  // -CM item. See: https://gitlab.com/senti-solutions/google-apps-scripts/-/issues/134

  // All tests passed, return true
  return true;
}
